import Guacamole from 'guacamole-common-js';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { debounceTime, first } from 'rxjs/operators';
import { useParams } from 'react-router';
import { ButtonView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import {
    useActiveUserContext,
    useAppConfigContext,
    useAuthServiceContext,
    useModalApiContext,
    useVirtualMachineApiContext
} from '@vivli/core/infrastructure/context';
import { Subject } from 'rxjs';
import { useTabFocusHook } from '@vivli/shared/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const getBoundingRect = () => document.body.getBoundingClientRect();

const containerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'relative'
};

const displayStyle: CSSProperties = {
    ...containerStyle,
    cursor: 'none'
};

const messageContainerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    cursor: 'default',
    backgroundColor: 'white',
    zIndex: 1
};

const messageStyle: CSSProperties = {
    display: 'grid',
    rowGap: '15px'
};

enum GuacStateEnum {
    Idle,
    Connecting,
    Waiting,
    Connected,
    Disconnecting,
    Disconnected
}

enum GuacTunnelStateEnum {
    Connecting,
    Open,
    Closed,
    Unstable
}

export const VirtualMachinePortalFeature = () => {
    const { virtualMachineId, dataRequestDoi, userId } = useParams();
    const virtualMachineService = useVirtualMachineApiContext();
    const modalService = useModalApiContext();
    const configService = useAppConfigContext();
    const { hasPermission } = useActiveUserContext();
    const authService = useAuthServiceContext();
    const timeoutRef = useRef<any>();
    const handleTabFocus = useTabFocusHook();

    const resizeSubjectRef = useRef(new Subject<void>());
    const activitySubjectRef = useRef(new Subject<void>());
    const combinedGuacStateRef = useRef(new Subject<{ clientState?: GuacStateEnum; tunnelState?: GuacTunnelStateEnum }>());
    const guacClientRef = useRef<Guacamole.Client>(null);
    const guacTunnelRef = useRef<Guacamole.WebSocketTunnel>(null);
    const guacClientStateRef = useRef<number>(GuacStateEnum.Disconnected);
    const displayRef = useRef<HTMLDivElement>(null);
    const activeModalRef = useRef<string>(null);
    const activeGuacDisplayRef = useRef<HTMLElement>();
    const guacInitRef = useRef<boolean>(false);

    const [disconnectedMsg, setDisconnectedMsg] = useState<string>(null);

    document.title = `Vivli - ${dataRequestDoi}`;

    const showOverlayMessage = (message: string, showLoading: boolean = true) => {
        activeModalRef.current && modalService.dismiss(activeModalRef.current);

        const modalComponent = showLoading ? (
            <LoadIndicatorCenteredView message={message} />
        ) : (
            <div style={messageContainerStyle}>{message.replace(/"/g, '')}</div>
        );

        activeModalRef.current = modalService.custom(modalComponent, {
            fullScreen: true
        });
    };

    const createGuacClient = () => {
        guacTunnelRef.current = new Guacamole.WebSocketTunnel(configService.guacUrl);
        guacClientRef.current = new Guacamole.Client(guacTunnelRef.current);
    };

    const isConnected = () => guacClientStateRef.current === GuacStateEnum.Connected && guacTunnelRef.current.isConnected();

    const handleKeyBinding = () => {
        const keyboard = new Guacamole.Keyboard(document.body);

        const fixKeys = (keysym) => {
            // 65508 - Right Ctrl
            // 65507 - Left Ctrl
            // somehow Right Ctrl is not sent, so send Left Ctrl instead
            if (keysym === 65508) return 65507;
            return keysym;
        };

        keyboard.onkeydown = function (keysym) {
            if (!isConnected()) return;

            guacClientRef.current.sendKeyEvent(1, fixKeys(keysym));
        };
        keyboard.onkeyup = function (keysym) {
            if (!isConnected()) return;

            guacClientRef.current.sendKeyEvent(0, fixKeys(keysym));
            activitySubjectRef.current.next();
        };

        // Mouse
        const mouse = new Guacamole.Mouse(document.body);
        mouse.onmousemove = function (mouseState) {
            if (!isConnected()) return;

            guacClientRef.current.sendMouseState(mouseState);
            activitySubjectRef.current.next();
        };
        mouse.onmousedown = mouse.onmouseup = function (mouseState) {
            if (!isConnected()) return;

            guacClientRef.current.sendMouseState(mouseState);
            activitySubjectRef.current.next();
        };
    };

    const parentOnClickHandler = () => {
        displayRef.current.focus();
    };

    const getGuacToken = () => {
        //TODO PERMFE - redo with permissions+
        if (hasPermission(PermissionsEnum.DrActAdv)) {
            // login to VM as specific authorized user
            if (userId) {
                return virtualMachineService.getUserGuacToken(virtualMachineId, userId);
            }

            return virtualMachineService.getAdminGuacToken(virtualMachineId);
        }

        return virtualMachineService.getGuacToken(virtualMachineId);
    };

    const handleGuacConnect = () => {
        getGuacToken()
            .pipe(first())
            .subscribe({
                next: (token) => {
                    const boundingRect = getBoundingRect();
                    const dpi = window.devicePixelRatio * 96 || 96;

                    guacClientRef.current.connect(
                        'token=' + token + '&width=' + boundingRect.width + '&height=' + boundingRect.height + '&dpi=' + dpi
                    );
                },
                error: (error) => {
                    showOverlayMessage(error, false);
                }
            });
    };

    const handleGuacConnected = () => {
        setDisconnectedMsg(null);

        activeGuacDisplayRef.current = guacClientRef.current.getDisplay().getElement();

        displayRef.current.appendChild(activeGuacDisplayRef.current);

        // wait to allow login to happen behind loading screen
        setTimeout(() => {
            modalService.dismiss(activeModalRef.current);
            activeModalRef.current = null;
        }, 2000);
    };

    const handleGuacStateChange = () => {
        guacTunnelRef.current.onstatechange = (tunnelState: GuacTunnelStateEnum) => {
            combinedGuacStateRef.current.next({ tunnelState, clientState: guacClientStateRef.current });
        };

        guacClientRef.current.onstatechange = (clientState: GuacStateEnum) => {
            // track client state changes since the guac client api does not have easy access to it
            guacClientStateRef.current = clientState;

            combinedGuacStateRef.current.next({ tunnelState: guacTunnelRef.current.state, clientState });
        };
    };

    const handleGuacInit = () => {
        createGuacClient();
        handleGuacStateChange();
        handleGuacConnect();
    };

    const handleDisconnect = () => {
        // disconnect fully from guacamole
        guacTunnelRef.current.disconnect();
        guacClientRef.current.disconnect();
    };

    const handleGuacReconnect = () => {
        handleDisconnect();
        handleGuacInit();
    };

    const handleReconnect = () => {
        showOverlayMessage('Reconnecting...');
        handleGuacReconnect();
    };

    const handleResizeDebounce = () => {
        resizeSubjectRef.current.next();
    };

    const handleWindowResize = () => {
        window.addEventListener('resize', handleResizeDebounce);
    };

    const handleTokenRefresh = () => {
        authService.getTokenSilent().then(() => {
            // no action necessary, this will refresh the users session to keep it alive
        });
    };

    const onTabFocus = () => {
        if (timeoutRef.current) {
            // if the timeout still exists, we are still connected to guac
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        } else {
            // 5m has elapsed, we are currently disconnected
            // automatically reconnect now that the tab is visible
            handleReconnect();
        }
    };

    const onTabBlur = () => {
        const timeoutInMs = configService.guacTimeoutMinutes * 60000;
        timeoutRef.current = setTimeout(() => {
            handleDisconnect();
            timeoutRef.current = null;
        }, timeoutInMs); // 5m timeout
    };

    const removeGuacDisplay = () => {
        if (activeGuacDisplayRef.current) {
            displayRef.current.removeChild(activeGuacDisplayRef.current);
        }
    };

    const handleConnectionState = (clientState: GuacStateEnum, tunnelState: GuacTunnelStateEnum) => {
        if (clientState === GuacStateEnum.Connected) {
            handleGuacConnected();
            handleKeyBinding();
        } else if (clientState === GuacStateEnum.Disconnected) {
            removeGuacDisplay();
            setDisconnectedMsg('You have been disconnected, please click the button below to reconnect.');
        } else if (tunnelState === GuacTunnelStateEnum.Closed) {
            // errors are not coming through correctly, subscribe to closed state to catch connection issues
            removeGuacDisplay();
            showOverlayMessage('Unable to access the Secure Research Environment. Please try again or contact support.', false);
        }
    };

    useEffect(() => {
        // force single instance only
        if (guacInitRef.current) return;
        guacInitRef.current = true;

        showOverlayMessage('Connecting...');

        handleTabFocus({
            onTabFocus,
            onTabBlur
        });

        handleGuacInit();
        handleWindowResize();

        const resizeSub = resizeSubjectRef.current.pipe(debounceTime(500)).subscribe(() => {
            showOverlayMessage('Resizing...');
            handleGuacReconnect();
        });

        activitySubjectRef.current.pipe(debounceTime(1000)).subscribe(() => {
            handleTokenRefresh();
        });

        combinedGuacStateRef.current.pipe(debounceTime(1000)).subscribe(({ clientState, tunnelState }) => {
            handleConnectionState(clientState, tunnelState);
        });

        return () => {
            window.removeEventListener('resize', handleResizeDebounce);
            resizeSub.unsubscribe();

            handleDisconnect();
        };
    }, []);

    return (
        <div style={containerStyle}>
            {disconnectedMsg && (
                <div style={messageContainerStyle}>
                    <div style={messageStyle}>
                        <div>{disconnectedMsg}</div>
                        <div>
                            <ButtonView onClick={handleReconnect}>Reconnect</ButtonView>
                        </div>
                    </div>
                </div>
            )}
            <div ref={displayRef} id="display" onClick={parentOnClickHandler} style={displayStyle} />
        </div>
    );
};
